import { Alert, Button, FormControlLabel, IconButton, Radio, RadioGroup, styled, Typography } from "@mui/material";
import { Suspense, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Pane } from "src/components/Pane";
import { useCaseShapeSelectionLayers } from "src/hooks";
import type { FeatureSelectionLayer } from "src/types";
import { parseWMSFeatureInfoParams } from "src/utils/ogc.utils";
import * as OGCService from "src/services/ogc.service";
import type { Feature, Polygon, MultiPolygon } from "geojson";
import { useTranslation } from "react-i18next";
import { ClearRounded } from "@mui/icons-material";
import { isPolygonOrMultiPolygon } from "src/utils/geometry.utils";

interface Props {
  selectedLayer: FeatureSelectionLayer | null;
  setSelectedLayer: (layer: FeatureSelectionLayer) => void;
  onShapesSelected: (features: Feature<Polygon | MultiPolygon>[]) => void;
  onPrevious: () => void;
  onNext: () => void;
  features: Feature<Polygon | MultiPolygon>[];
  map: L.Map | null;
  runYear: number;
}

export function CasePickShape(props: Props) {
  const { t } = useTranslation();

  return (
    <Styles>
      <Typography variant="h4" color="primary">
        {t("label.pickACase")}
      </Typography>
      <ErrorBoundary fallback={null}>
        <Suspense fallback>
          <LoadData {...props} />
        </Suspense>
      </ErrorBoundary>
    </Styles>
  );
}

function LoadData(props: Props) {
  const { selectedLayer, setSelectedLayer, onShapesSelected, map, features, onNext, onPrevious, runYear } = props;
  const { t } = useTranslation();
  const { data: layers } = useCaseShapeSelectionLayers(runYear);
  const layer = selectedLayer || layers?.at(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!map) return;

    async function handleClick(event: L.LeafletMouseEvent) {
      setIsLoading(true);
      const clickWMSParams = parseWMSFeatureInfoParams(event);
      const featureInfo = await OGCService.getWMSFeatureInfo({
        ...clickWMSParams,
        layers: layer?.geoserverLayer || "",
        query_layers: layer?.geoserverLayer || "",
      });
      setIsLoading(false);
      const clickedFeature = featureInfo.features[0];
      if (!clickedFeature || !isPolygonOrMultiPolygon(clickedFeature)) return;
      const isCtrlOrCmdPressed = event.originalEvent.ctrlKey || event.originalEvent.metaKey;
      if (isCtrlOrCmdPressed) {
        const featureId = getFeatureId(clickedFeature);
        const existingIndex = features.findIndex((f) => getFeatureId(f) === featureId);
        if (existingIndex >= 0) {
          const newFeatures = features.filter((_, idx) => idx !== existingIndex);
          onShapesSelected(newFeatures);
        } else {
          onShapesSelected([...features, clickedFeature]);
        }
      } else {
        onShapesSelected([clickedFeature]);
      }
    }
    map.on("click", handleClick);

    return () => {
      map.off("click", handleClick);
    };
  }, [map, layer, onShapesSelected, features]);

  function getFeatureId(feature: Feature<Polygon | MultiPolygon>): string {
    return JSON.stringify(feature.properties);
  }

  function clearSelection() {
    onShapesSelected([]);
  }
  return (
    <>
      <RadioGroup
        value={layer?.id}
        onChange={(e) => {
          const layer = layers?.find((layer) => layer.id === Number.parseInt(e.target.value));
          if (!layer) return;
          setSelectedLayer(layer);
          clearSelection();
        }}
      >
        {(layers || []).map((layer) => (
          <FormControlLabel key={layer.id} value={layer.id} control={<Radio />} label={layer.nameTranslation} />
        ))}
      </RadioGroup>

      {features.length === 0 && !isLoading && (
        <div>
          <Typography variant="body1">{t("label.selectCaseOnMap")}</Typography>
        </div>
      )}

      <Alert severity="info">
        {t("label.holdAndClickToSelectMultipleShapes", {
          modifier: navigator.userAgent.includes("Mac") ? "⌘ Cmd" : "Ctrl",
        })}
      </Alert>
      {features.length > 0 && (
        <div className="list-container">
          {features.map((feature, idx) => (
            <div key={idx} className="feature-card">
              {layer?.propertyList.map((p) => (
                <div key={p.id} className="list-item">
                  <Typography variant="body1">{p.nameTranslation}:</Typography>
                  <Typography variant="body1" color="primary">
                    {feature.properties?.[p.nameKey.toUpperCase()]}
                  </Typography>
                </div>
              ))}
              <IconButton
                size="small"
                onClick={() => {
                  const newFeatures = features.filter((_, i) => i !== idx);
                  onShapesSelected(newFeatures);
                }}
              >
                <ClearRounded fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      {isLoading && <div>{t("label.loading")}</div>}

      <div className="button-container">
        <Button variant="text" onClick={onPrevious}>
          {t("label.previous")}
        </Button>
        <Button variant="contained" onClick={onNext} disabled={features.length === 0}>
          {t("label.next")}
        </Button>
      </div>
    </>
  );
}

const Styles = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};

  .list-container {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    width: 100%;
  }

  .list-item {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    row-gap: ${({ theme }) => theme.spacing(1)};
  }

  .feature-card {
    width: 100%;
    padding: 16px 32px;
    border-radius: 4px;
    position: relative;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    .MuiIconButton-root {
      position: absolute;
      top: 4px;
      right: 4px;
      color: ${({ theme }) => theme.palette.grey[500]};
      .MuiSvgIcon-root {
        font-size: 18px;
      }
    }
  }

  .button-container {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing(1)};
    justify-content: flex-end;
  }
`;
